/**
 * Data layer for `campaign-read-endpoint`.
 *
 * Thin, typed wrappers over `/api/v1/campaign-read-endpoint` that honour the
 * dash contract D6: raw `fetch()` is confined to this file. All requests
 * carry `x-tenant-id: t1` (single-tenant dash default) and an `accept` header
 * so the api layer can surface `{error}` envelopes verbatim.
 */

export interface CampaignReadEndpointRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCampaignReadEndpointResponse {
	count: number;
	items: CampaignReadEndpointRow[];
	next_cursor?: string | null;
}

export interface CreateCampaignReadEndpointBody {
	name: string;
	status?: "active" | "paused" | "archived";
	[key: string]: unknown;
}

const BASE = "/api/v1/campaign-read-endpoint";
const TENANT = "t1";

function tenantHeaders(extra?: Record<string, string>): Record<string, string> {
	return {
		accept: "application/json",
		"x-tenant-id": TENANT,
		...(extra ?? {}),
	};
}

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCampaignReadEndpointResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: tenantHeaders(),
	});
	return parseOrThrow<ListCampaignReadEndpointResponse>(res);
}

export async function create(
	body: CreateCampaignReadEndpointBody,
): Promise<CampaignReadEndpointRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: tenantHeaders({ "content-type": "application/json" }),
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CampaignReadEndpointRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: tenantHeaders(),
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const campaignReadEndpointQueryKeys = {
	all: ["campaign-read-endpoint"] as const,
	list: () => [...campaignReadEndpointQueryKeys.all] as const,
	detail: (id: string) => [...campaignReadEndpointQueryKeys.all, id] as const,
};
